// WhatsApp Cloud API sendable media MIME types (doc 05 §4). This is the EXACT set
// Meta accepts on POST /{phone_number_id}/media — sending anything else fails with
// Graph error (#100) "Param file must be a file with one of the following types…".
// Kept isomorphic (browser + server) so the compose UI can reject unsupported files
// BEFORE upload instead of failing silently at Meta after the fact.
//
// Notably absent and commonly hit: video/quicktime (iPhone .MOV), image/gif,
// image/heic/heif (iPhone photos — the browser usually re-encodes these to JPEG),
// application/epub+zip, application/zip.
export const WHATSAPP_MEDIA_MIME = {
    image: ["image/jpeg", "image/png"],
    video: ["video/mp4", "video/3gpp"],
    audio: ["audio/aac", "audio/mp4", "audio/mpeg", "audio/amr", "audio/ogg", "audio/opus"],
    document: [
        "application/pdf",
        "text/plain",
        "application/vnd.ms-powerpoint",
        "application/msword",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
};
/** Flat set of every MIME WhatsApp will accept for an outbound media send. */
export const WHATSAPP_SENDABLE_MIME = new Set(Object.values(WHATSAPP_MEDIA_MIME).flat());
/** True when a file's MIME can be sent as-is via the WhatsApp Cloud API. */
export function isWhatsAppSendableMime(mime) {
    return mime != null && WHATSAPP_SENDABLE_MIME.has(mime.toLowerCase());
}
/**
 * True for a video the server must transcode to MP4 before sending (e.g. iPhone
 * `.MOV` / HEVC). MP4 and 3GP go straight through.
 */
export function needsTranscodeToMp4(mime) {
    const m = (mime ?? "").toLowerCase();
    return m.startsWith("video/") && m !== "video/mp4" && m !== "video/3gpp";
}
/**
 * A short, human-friendly reason a file can't be sent, or null when it's fine.
 * Covers the types users actually hit (iPhone .MOV, GIF, HEIC, epub, zip) with
 * actionable guidance; everything else gets a generic "not supported" line.
 */
export function unsupportedMediaReason(mime) {
    const m = (mime ?? "").toLowerCase();
    if (isWhatsAppSendableMime(m))
        return null;
    // WebP images are converted to PNG by the server before sending.
    if (m === "image/webp")
        return null;
    // Any other video is transcoded to MP4 server-side, so it's acceptable to pick.
    if (m.startsWith("video/"))
        return null;
    if (m === "image/gif")
        return "WhatsApp doesn't accept GIF files here. Send it as an MP4 video instead.";
    if (m === "image/heic" || m === "image/heif")
        return "This photo is in HEIC format. Save or export it as JPEG, then send.";
    if (m.startsWith("image/"))
        return "Choose a JPEG, PNG, or WebP image.";
    return "WhatsApp doesn't support this file type. Try a PDF, Office document, image, MP4 video, or audio file.";
}
//# sourceMappingURL=media.js.map